/**
 * RadiusServersList — Servidores RADIUS (estándar de listados del CMDB).
 *
 * Antes: tabla propia (`wol-table`), modal de borrado propio, badges con paleta
 * clara a mano y la ficha abriendo **directamente en edición**.
 *
 * Ahora: `ListPage` con buscador, filtros, columnas, vistas y export; la ficha
 * entra en **lectura** (con botón Editar) y el borrado usa el diálogo único del
 * CMDB (avisando de los SSIDs que se quedarán sin servidor).
 *
 * Botones propios:
 *  · **Ver los SSIDs que lo usan** — lista los SSIDs con `radius_server_id`
 *    apuntando aquí (filtro añadido al endpoint de SSIDs).
 *  · **Ver servicio** — al Diseñador de Servicios con ese servicio (el RADIUS es
 *    un servicio AAA de la CMDB y su host puede venir derivado de él).
 *
 * Ojo: el modelo NO guarda secretos RADIUS (lo dice expresamente), así que no
 * hay nada que ocultar en la tabla.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LockClosedIcon,
  ListBulletIcon,
  Squares2X2Icon,
  WifiIcon,
  Square3Stack3DIcon,
} from '@heroicons/react/24/outline';
import ListPage from './common/ListPage';
import Dialog from './common/Dialog';
import RadiusServerForm from './RadiusServerForm';
import { useAutoOpen } from '../hooks/useAutoOpen';
import { useListView } from '../views/ViewsContext';
import { getServerFormStyle } from '../utils/serverFormStyle';
import api from '../services/api';
import useNavOrder from '../hooks/useNavOrder';

const STATUS_META = {
  active:   { label: 'Activo',   badge: 'list-badge--on' },
  inactive: { label: 'Inactivo', badge: 'list-badge--off' },
};

export default function RadiusServersList({ companyId }) {
  const { t } = useTranslation();
  const tr = useCallback((key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  }, [t]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [servers, setServers] = useState([]);
  const estiloFicha = getServerFormStyle();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterUse, setFilterUse] = useState('');
  const [filterService, setFilterService] = useState('');
  const [hlId, setHlId] = useState(null);

  /* ficha / alta */
  const [selected, setSelected] = useState(null);      // null = lista
  const [initialMode, setInitialMode] = useState('edit');

  /* SSIDs que usan un servidor (diálogo) */
  const [ssidServer, setSsidServer] = useState(null);
  const [ssids, setSsids] = useState([]);
  const [ssidsLoading, setSsidsLoading] = useState(false);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.getWifiRadiusServers(companyId);
      setServers(res.data || []);
    } catch (e) {
      setError(e.message || 'Error cargando los servidores RADIUS');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  /* Vuelta desde otra pantalla (DocsEditor, diseñador…): `location.state.openId` */
  useAutoOpen(servers, (srv) => { setInitialMode('view'); setSelected(srv); });

  /* ?destacar=<id>[&abrir=1] — convención del CMDB */
  useEffect(() => {
    const raw = searchParams.get('destacar');
    if (!raw || !servers.length) return undefined;
    const id = Number(raw);
    const target = servers.find(s => s.id === id);
    if (!target) return undefined;
    const abrir = searchParams.get('abrir') === '1';
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('destacar');
      next.delete('abrir');
      return next;
    }, { replace: true });
    if (abrir) { setInitialMode('view'); setSelected(target); return undefined; }
    setHlId(id);
    const row = document.getElementById(`radius-row-${id}`);
    if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = setTimeout(() => setHlId(null), 4000);
    return () => clearTimeout(timer);
  }, [searchParams, servers, setSearchParams]);

  /* ── ficha ──────────────────────────────────────────────────────────────── */
  const openView = (srv) => { setInitialMode('view'); setSelected(srv); };
  const openEdit = (srv) => { setInitialMode('edit'); setSelected(srv); };
  const openCreate = () => { setInitialMode('create'); setSelected({}); };

  const closeForm = () => { setSelected(null); load(); };

  const handleDelete = async (srv) => {
    try {
      await api.deleteWifiRadiusServer(srv.id, companyId);
      await load();
    } catch (e) {
      setError(e.message || 'Error eliminando el servidor RADIUS');
    }
  };

  /* ── SSIDs que usan el servidor ─────────────────────────────────────────── */
  const openSsids = async (srv) => {
    setSsidServer(srv);
    setSsids([]);
    setSsidsLoading(true);
    try {
      const res = await api.getWifiSSIDs(companyId, { radius_server_id: srv.id });
      setSsids(res.data || []);
    } catch (e) {
      setError(e.message || 'Error cargando los SSIDs');
    } finally {
      setSsidsLoading(false);
    }
  };

  /* ── filtrado (el endpoint solo filtra por empresa: se hace en cliente) ─── */
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return servers.filter((s) => {
      if (filterStatus && (s.status || 'active') !== filterStatus) return false;
      if (filterUse === 'with' && !(s.ssid_count > 0)) return false;
      if (filterUse === 'without' && s.ssid_count > 0) return false;
      if (filterService === 'with' && !s.service_id) return false;
      if (filterService === 'without' && s.service_id) return false;
      if (!needle) return true;
      return (s.name || '').toLowerCase().includes(needle)
        || (s.host || '').toLowerCase().includes(needle)
        || (s.description || '').toLowerCase().includes(needle)
        || (s.service_name || '').toLowerCase().includes(needle)
        || String(s.auth_port || '').includes(needle)
        || String(s.acct_port || '').includes(needle);
    });
  }, [servers, search, filterStatus, filterUse, filterService]);

  // Orden de las fichas congelado mientras la ficha está abierta.
  const navRadius = useNavOrder(filtered, selected, selected !== null);

  /* ── columnas ───────────────────────────────────────────────────────────── */
  const columns = useMemo(() => {
    const dash = <span className="list-hint">—</span>;
    return [
      {
        key: 'name',
        label: tr('radius.name', 'Nombre'),
        sortable: true,
        render: (s) => (
          <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
            <span style={{ fontWeight: 600 }}>{s.name}</span>
            {s.description && (
              <span className="list-hint" title={s.description}>
                {s.description.length > 70 ? `${s.description.slice(0, 70)}…` : s.description}
              </span>
            )}
          </span>
        ),
      },
      {
        key: 'host',
        label: 'Host / IP',
        sortable: true,
        render: (s) => (s.host ? <span className="list-code">{s.host}</span> : dash),
      },
      {
        key: 'auth_port',
        label: tr('radius.auth', 'Auth (UDP)'),
        sortable: true,
        sortValue: (s) => s.auth_port ?? 0,
        render: (s) => (s.auth_port ? <span className="list-code">{s.auth_port}</span> : dash),
      },
      {
        key: 'acct_port',
        label: tr('radius.acct', 'Acct (UDP)'),
        sortable: true,
        sortValue: (s) => s.acct_port ?? 0,
        render: (s) => (s.acct_port ? <span className="list-code">{s.acct_port}</span> : dash),
      },
      {
        key: 'service_name',
        label: tr('radius.service', 'Servicio'),
        sortable: true,
        sortValue: (s) => s.service_name || '',
        render: (s) => (s.service_id ? (
          <button
            type="button"
            className="list-badge list-badge--warn"
            style={{ cursor: 'pointer' }}
            title={tr('radius.view_service', 'Ver este servicio en el Diseñador')}
            onClick={(event) => { event.stopPropagation(); navigate(`/services/designer?service=${s.service_id}`); }}
          >
            {s.service_name || `#${s.service_id}`}
          </button>
        ) : dash),
      },
      {
        key: 'ssid_count',
        label: 'SSIDs',
        sortable: true,
        sortValue: (s) => s.ssid_count ?? 0,
        render: (s) => (s.ssid_count > 0
          ? (
            <button
              type="button"
              className="list-badge list-badge--on"
              style={{ cursor: 'pointer' }}
              title={tr('radius.view_ssids', 'Ver los SSIDs que usan este servidor')}
              onClick={(event) => { event.stopPropagation(); openSsids(s); }}
            >
              {s.ssid_count}
            </button>
          )
          : <span className="list-badge list-badge--off">0</span>),
      },
      {
        key: 'status',
        label: tr('ipam.status', 'Estado'),
        sortable: true,
        sortValue: (s) => s.status || 'active',
        render: (s) => {
          const meta = STATUS_META[s.status] || STATUS_META.active;
          return <span className={`list-badge ${meta.badge}`}>{meta.label}</span>;
        },
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, tr, navigate]);

  const defaultVisible = ['name', 'host', 'auth_port', 'acct_port', 'service_name', 'ssid_count', 'status'];

  const filters = [
    {
      key: 'status', label: tr('ipam.status', 'Estado'), allLabel: tr('ipam.status_all', 'Todos los estados'),
      value: filterStatus, onChange: setFilterStatus,
      options: Object.entries(STATUS_META).map(([value, meta]) => ({ value, label: meta.label })),
    },
    {
      key: 'use', label: tr('radius.use', 'Uso'), allLabel: tr('radius.use_all', 'En uso y sin uso'),
      value: filterUse, onChange: setFilterUse,
      options: [
        { value: 'with', label: tr('radius.use_with', 'Con SSIDs que lo usan') },
        { value: 'without', label: tr('radius.use_without', 'Sin uso') },
      ],
    },
    {
      key: 'service', label: tr('radius.service', 'Servicio'), allLabel: tr('radius.service_all', 'Con y sin servicio'),
      value: filterService, onChange: setFilterService,
      options: [
        { value: 'with', label: tr('radius.service_with', 'Con servicio asociado') },
        { value: 'without', label: tr('radius.service_without', 'Sin servicio') },
      ],
    },
  ];

  const subtitle = `${servers.length} ${tr('radius.plural', 'servidores')}`
    + ` · ${filtered.length} ${tr('location.filtered', 'filtrados')}`;

  const exportRows = useMemo(() => filtered.map(s => ({
    'Nombre': s.name || '',
    'Descripción': s.description || '',
    'Host / IP': s.host || '',
    'Puerto auth (UDP)': s.auth_port ?? '',
    'Puerto accounting (UDP)': s.acct_port ?? '',
    'Servicio (CMDB)': s.service_name || '',
    'SSIDs asignados': s.ssid_count ?? 0,
    'Estado': (STATUS_META[s.status] || STATUS_META.active).label,
  })), [filtered]);

  /** Tarjetas: lo esencial del servidor, con los mismos botones de fila. */
  function renderCards({ rows, rowActions }) {
    return (
      <div className="list-cards">
        {rows.map((s) => {
          const meta = STATUS_META[s.status] || STATUS_META.active;
          return (
            <div
              key={s.id}
              id={`radius-card-${s.id}`}
              className={`list-card${s.id === hlId ? ' list-row-highlight' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => openView(s)}
              onKeyDown={(event) => { if (event.key === 'Enter') openView(s); }}
              title={`${tr('action.view', 'Ver')} ${s.name}`}
            >
              <div className="list-card-head">
                <div>
                  <div className="list-card-title">{s.name}</div>
                  {s.host && <span className="list-code">{s.host}</span>}
                </div>
                <span className={`list-badge ${meta.badge}`}>{meta.label}</span>
              </div>

              <div className="list-card-meta">
                <span className="list-badge">Auth {s.auth_port ?? '—'}</span>
                <span className="list-badge">Acct {s.acct_port ?? '—'}</span>
                {s.service_name && <span>{tr('radius.service', 'Servicio')}: {s.service_name}</span>}
                <span className="list-badge">{s.ssid_count || 0} SSIDs</span>
              </div>

              {s.description && <div className="list-card-meta"><span>{s.description}</span></div>}

              <div className="list-card-actions">{rowActions(s)}</div>
            </div>
          );
        })}
      </div>
    );
  }

  const RADIUS_VIEWS = useMemo(() => [
    { key: 'list', label: tr('ipam.view_list', 'Lista'), icon: ListBulletIcon, title: tr('ipam.view_list_hint', 'Tabla con todas las columnas') },
    { key: 'card', label: tr('ipam.view_cards', 'Fichas'), icon: Squares2X2Icon, title: tr('ipam.view_cards_hint', 'Tarjetas con lo esencial de cada servidor'), render: renderCards },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, hlId]);
  useListView('radius_servers', 'list');

  if (!companyId) {
    return (
      <div className="list-page" style={{ padding: '1.5rem' }}>
        <div className="alert alert-warning">
          {tr('radius.select_company', 'Selecciona una empresa para ver los servidores RADIUS.')}
        </div>
      </div>
    );
  }

  return (
    <>
      {selected !== null && (
        <RadiusServerForm
          server={selected && selected.id ? selected : null}
          companyId={companyId}
          initialMode={initialMode}
          variant={estiloFicha === 'cards' ? 'cards' : 'classic'}
          navItems={navRadius}
          navIndex={selected && selected.id ? navRadius.findIndex(s => String(s.id) === String(selected.id)) : -1}
          onNavigate={(destino) => setSelected(destino)}
          onClose={closeForm}
          onSave={(creado) => {
            load();
            // Alta: el listado adopta el registro creado y la ficha sigue abierta.
            if (creado?.id) { setInitialMode('edit'); setSelected(creado); }
          }}
        />
      )}

      <div style={{ display: selected !== null ? 'none' : undefined }}>
        <ListPage
          icon={LockClosedIcon}
          title={tr('radius.title', 'Servidores RADIUS')}
          subtitle={subtitle}

          onCreate={openCreate}
          createLabel={tr('radius.new', 'Nuevo servidor RADIUS')}
          createTitle={tr('radius.new_hint', 'Servidor AAA para los SSIDs con autenticación enterprise')}
          createDisabled={!companyId}

          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={tr('radius.search', 'Buscar por nombre, host, servicio…')}
          filters={filters}

          exportRows={exportRows}
          exportFileBase="radius"
          exportSheetName="Servidores RADIUS"

          columns={columns}
          columnStorageKey="cmdb_cols_radius"
          companyId={companyId}
          defaultVisible={defaultVisible}
          rows={filtered}
          rowKey={(s) => s.id}
          rowId={(s) => `radius-row-${s.id}`}
          rowClassName={(s) => (s.id === hlId ? 'list-row-highlight' : '')}
          onRowClick={openView}
          rowTitle={(s) => `${tr('action.view', 'Ver')} ${s.name}`}

          views={RADIUS_VIEWS}
          listKey="radius_servers"
          defaultView="list"

          loading={loading}
          loadingText={tr('msg.loading', 'Cargando servidores RADIUS…')}
          error={error}

          empty={{
            icon: '🔐',
            title: servers.length === 0
              ? tr('radius.empty', 'No hay servidores RADIUS')
              : tr('msg.no_results', 'Ningún servidor coincide con los filtros'),
            subtitle: servers.length === 0
              ? tr('radius.empty_hint', 'Defínelos para usarlos en los SSIDs con autenticación enterprise (WPA2/WPA3-Enterprise).')
              : undefined,
            action: servers.length === 0 ? (
              <button type="button" className="list-btn-primary" onClick={openCreate}>
                ＋ {tr('radius.new', 'Nuevo servidor RADIUS')}
              </button>
            ) : undefined,
          }}

          actions={{
            extra: (s) => (
              <>
                {/* Ver los SSIDs que lo usan */}
                <button
                  type="button"
                  className="list-action-btn"
                  disabled={!(s.ssid_count > 0)}
                  onClick={() => openSsids(s)}
                  title={tr('radius.view_ssids', 'Ver los SSIDs que usan este servidor')}
                >
                  <WifiIcon />
                </button>
                {/* Ver el servicio AAA en el Diseñador */}
                <button
                  type="button"
                  className="list-action-btn"
                  disabled={!s.service_id}
                  onClick={() => navigate(`/services/designer?service=${s.service_id}`)}
                  title={tr('radius.view_service', 'Ver este servicio en el Diseñador')}
                >
                  <Square3Stack3DIcon />
                </button>
              </>
            ),
            onEdit: openEdit,
            onDelete: handleDelete,
            deleteTitle: tr('radius.delete_title', 'Eliminar servidor RADIUS'),
            deleteMessage: (s) => (
              <>
                {tr('radius.delete_confirm', '¿Seguro que quieres eliminar')} <strong>{s.name}</strong>
                {s.host ? ` (${s.host}:${s.auth_port})` : ''}?{' '}
                {s.ssid_count > 0
                  ? tr('radius.delete_used', `Ojo: ${s.ssid_count} SSID(s) usan este servidor y se quedarán sin RADIUS asignado.`)
                  : tr('ip.delete_warn', 'No se puede deshacer.')}
              </>
            ),
          }}
        />
      </div>

      {/* ── SSIDs que usan el servidor ─────────────────────────────────── */}
      {ssidServer && (
        <Dialog
          title={`${tr('radius.ssids_of', 'SSIDs que usan')} ${ssidServer.name}`}
          icon={<WifiIcon style={{ width: 18, height: 18 }} />}
          maxWidth={640}
          onClose={() => setSsidServer(null)}
          footer={(
            <button type="button" className="list-btn-secondary" onClick={() => setSsidServer(null)}>
              {tr('action.close', 'Cerrar')}
            </button>
          )}
        >
          {ssidsLoading && <p style={{ margin: 0 }}>{tr('msg.loading', 'Cargando…')}</p>}
          {!ssidsLoading && ssids.length === 0 && (
            <p style={{ margin: 0 }} className="list-hint">
              {tr('radius.no_ssids', 'Ningún SSID apunta a este servidor.')}
            </p>
          )}
          {!ssidsLoading && ssids.length > 0 && (
            <table className="table table-sm align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>SSID</th>
                  <th>{tr('radius.security', 'Seguridad')}</th>
                  <th>VLAN</th>
                  <th>{tr('radius.band', 'Banda')}</th>
                  <th>{tr('ipam.status', 'Estado')}</th>
                </tr>
              </thead>
              <tbody>
                {ssids.map(s => (
                  <tr key={s.id}>
                    <td>
                      <span style={{ fontWeight: 600 }}>{s.name}</span>
                      {s.hidden && <span className="list-badge ms-2">{tr('radius.hidden', 'Oculto')}</span>}
                    </td>
                    <td><span className="list-badge">{s.security_mode_label || s.security_mode}</span></td>
                    <td>{s.vlan_id != null ? <span className="list-code">{s.vlan_id}</span> : <span className="list-hint">—</span>}</td>
                    <td>{s.band_filter_label || s.band_filter || <span className="list-hint">—</span>}</td>
                    <td><span className="list-badge">{s.status_label || s.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Dialog>
      )}
    </>
  );
}
